import { BuildingLibraryIcon, CreditCardIcon, EyeIcon } from '@heroicons/react/24/outline'
import { AUTH_TOKEN_SET } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import type { InputType } from 'config/input.type.constants'
import jwtDecode from 'jwt-decode'
import { TemporaryLayout } from 'layouts'
import { FeasibilityHistory, StripeIntentDialog, StripePay, StripeStatusLabels } from 'pages/Loan'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  getExtensionSigningPackage,
  getOneLoanServiceExtension,
  getStripeIntent,
  PaymentOrderType,
  signExtensionAgreement,
  StripeIntentBlockingStatus,
  updateLoanServiceExtension,
} from 'services'
import { PlainTable } from 'stories/components'
import { formatTime, InputConvert, InputValidate, removeComma } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { extensionInputLogic, loanExtensionInputs } from './constant'

export default function LoanServicingExtensionSign() {
  const urlParams: any = useParams()
  const { token, id } = urlParams
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>(loanExtensionInputs(true))
  const [histories, setHistories] = useState<FeasibilityHistory[]>([])
  const [stripeIntent, setStripeIntent] = useState<Record<string, any> | null>(null)
  const [isPaymentLocked, setPaymentLocked] = useState(false)
  const [payData, setPayData] = useState<Record<string, any> | null>(null)
  const dispatch = useDispatch()
  const [requestedBy, setRequestedBy] = useState<any>({})
  const [refId, setRefId] = useState()
  const [extensionStatus, setExtensionStatus] = useState<Record<string, any> | null>(null)
  const [signature, setSignature] = useState<Record<string, any> | null>(null)
  const [signerName, setSignerName] = useState('')
  const [consented, setConsented] = useState(false)
  // The emailed link outlives the request it points at: an extension deleted from the loan's
  // Extension tab takes its record with it, and a borrower holding the email still arrives here.
  // Without this the failed load left the page on its spinner for good, under two red toasts.
  const [unavailable, setUnavailable] = useState(false)

  useEffect(() => {
    setLoanNumber()
    const payload: any = jwtDecode(token)

    dispatch({
      type: AUTH_TOKEN_SET,
      token,
      user: payload,
    })

    setRefId(payload.refId)

    load()
  }, [])

  const load = async () => {
    setLoading(true)
    // Two independent reads, each tolerant of the other failing: the signature package is an extra,
    // so its failure must not cost the borrower the payment screen, and vice versa.
    const [res, pkg] = await Promise.all([
      getOneLoanServiceExtension(id).catch(() => null),
      getExtensionSigningPackage(id).catch(() => null),
    ])

    if (!res) {
      setUnavailable(true)
      setLoading(false)
      setLoaded(true)
      return
    }

    if (pkg?.success && pkg.signature) {
      setSignature(pkg.signature)
      const me = (pkg.signature.signers || [])[pkg.signature.signerIndex]
      if (me?.name) setSignerName(me.name)
    }

    if (res.success) {
      const { data, by, histories, status } = res.data
      let newInputs = cloneDeep(inputs)
      Object.keys(data).forEach((key) => {
        if (newInputs[key]) {
          newInputs[key].value = data[key]
        }
      })

      newInputs.documents.disableNameChange = true

      newInputs = extensionInputLogic(newInputs)

      setInputs(newInputs)
      setHistories(histories)
      setRequestedBy(by)
      setExtensionStatus(status || null)
    }

    setLoading(false)
    setLoaded(true)
  }

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].value = value
    newInputs[key].error = InputValidate(newInputs[key])
    newInputs = extensionInputLogic(newInputs)
    setInputs(newInputs)
  }

  useEffect(() => {
    let toEmail = ''
    try {
      let emailObj = JSON.parse(inputs['feasibilityEmailToLink']?.value || '{}')
      Object.keys(emailObj).map((key) => {
        if (emailObj[key] === 'to') toEmail = key
      })
    } catch (e) {
      toEmail = inputs['feasibilityEmailToLink']?.value
    }
    setPayData({
      loanExtensionId: id,
      type: inputs['feasibilityType']?.value,
      email: toEmail,
      amount: removeComma(inputs['paymentAmount']?.value),
      refId,
      payOrderType: PaymentOrderType.LoanExtension,
    })
  }, [inputs['feasibilityType']?.value, inputs['paymentAmount']?.value, refId])

  const renderStatus = (status: string) => {
    if (!StripeStatusLabels[status]) return status
    return <p className={`font-medium text-${StripeStatusLabels[status][0]}-500`}>{StripeStatusLabels[status][1]}</p>
  }

  const onOpenHistory = async (history: FeasibilityHistory, index: number) => {
    setLoading(true)
    const stripeIntent = await getStripeIntent(history.data.intentId)
    const { status } = stripeIntent

    if (status == 'requires_action') {
      const url = stripeIntent.response.next_action.verify_with_microdeposits.hosted_verification_url
      var windowReference: any = window.open()
      windowReference.location = url
    } else {
      setStripeIntent({
        index,
        ...stripeIntent,
      })
    }
    setLoading(false)
  }

  const historiesTableData = useMemo(
    () =>
      histories.map((history, index) => [
        index + 1,
        <p className="flex gap-1 items-center">
          {history.type == 'ACH' ? (
            <BuildingLibraryIcon className="text-shade-blue w-4 h-4" />
          ) : (
            <CreditCardIcon className="text-shade-blue w-4 h-4" />
          )}
          {history.type}
        </p>,
        renderStatus(history.data.status),
        formatTime(history.createdAt),
        <button
          className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
          onClick={() => onOpenHistory(history, index + 1)}
        >
          <EyeIcon className="w-4 h-4" />
        </button>,
      ]),
    [histories],
  )

  useEffect(() => {
    const hasBlockingPayHistory = histories.some(
      (history: any) => history.method == 'Pay' && StripeIntentBlockingStatus.includes(history.data.status),
    )
    const hasBlockingExtensionStatus = [
      'Paid',
      'Pending Payment',
      'Failed',
      'Cancelled',
      'Requires Action',
    ].includes(extensionStatus?.status)
    if (hasBlockingPayHistory || hasBlockingExtensionStatus) setPaymentLocked(true)
  }, [histories, extensionStatus])

  const onPay = (result: boolean) => {
    if (!result) return

    let data = {
      feasibilityType: inputs['feasibilityType'].value,
      documents: inputs['documents'].value,
      carrier: inputs['carrier'].value,
      trackingNumber: inputs['trackingNumber'].value,
    }

    updateLoanServiceExtension(data, id)

    setTimeout(async () => {
      const { data } = await getOneLoanServiceExtension(id)
      setHistories(data.histories)
      setExtensionStatus(data.status || null)
    }, 2000)
    setPaymentLocked(true)
  }

  const onSign = async () => {
    if (!signerName.trim()) {
      toast('Type your full name to sign.', { type: 'error' })
      return
    }
    setLoading(true)
    try {
      const res = await signExtensionAgreement(id, signerName.trim())
      if (res.success) {
        toast(res.allSigned ? 'Agreement signed. Thank you.' : 'Your signature is recorded.', { type: 'info' })
        await load()
      } else toast('Signing failed. Please try again or contact us.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const signatureBlock = () => {
    if (!signature) return null
    const isSigner = signature.signerIndex >= 0
    return (
      <div className="relative bg-white shadow1 rounded mb-6 p-4">
        <div className="font-bold mb-2">Extension Agreement</div>
        {signature.url && (
          <a
            href={signature.url}
            target="_blank"
            rel="noreferrer"
            className="text-shade-blue hover:underline text-[14px]"
          >
            Open the agreement ({signature.templateName})
          </a>
        )}
        <div className="mt-3 text-[13px] text-gray-600">
          {(signature.signers || []).map((s: any, index: number) => (
            <div key={index}>
              {s.name} - {s.signed ? `signed ${formatTime(s.signedAt)}` : 'not yet signed'}
            </div>
          ))}
        </div>
        {!isSigner && (
          <div className="mt-4 text-[14px] text-gray-700">This agreement is not awaiting your signature.</div>
        )}
        {isSigner && signature.alreadySigned && (
          <div className="mt-4 w-fit bg-green-50 border border-green-300 text-green-800 px-4 py-3 rounded text-[14px]">
            You have signed this agreement.
          </div>
        )}
        {isSigner && !signature.alreadySigned && (
          <div className="mt-4 max-w-[560px]">
            <label className="flex items-start gap-2 text-[13px] text-gray-700 mb-3">
              <input
                type="checkbox"
                checked={consented}
                onChange={(e) => setConsented(e.target.checked)}
                className="mt-1"
              />
              <span>
                I have read the agreement above and I consent to sign it electronically. Typing my name below is my
                legally binding signature.
              </span>
            </label>
            <input
              type="text"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Type your full name"
              className="border rounded px-3 py-2 w-full mb-3 text-[14px]"
            />
            <button
              type="button"
              disabled={!consented || !signerName.trim()}
              onClick={onSign}
              className={`rounded px-4 py-2 text-white text-[14px] ${
                consented && signerName.trim() ? 'bg-shade-blue cursor-pointer' : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              Sign Agreement
            </button>
          </div>
        )}
      </div>
    )
  }

  if (!loaded) return <LayoutLoading show />

  if (unavailable)
    return (
      <TemporaryLayout noOverview={true}>
        <div className="LoanServicingExtensionSign-container">
          <div className="px-2 mt-6">
            <Overview title={`Loan Extension - ${id}`} hasBackButton={false} />
            <div className="max-w-screen-2xl m-auto">
              <div className="relative bg-white shadow1 rounded mb-6 p-4">
                <div
                  className="w-fit bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative text-[14px]"
                  role="alert"
                >
                  <div className="font-variation-settings-600">This extension request is no longer available.</div>
                  <div className="mt-1">
                    The request this link was sent for has been withdrawn or replaced, so there is nothing to pay here.
                    Please contact your loan servicer, who can send you a new link.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </TemporaryLayout>
    )

  return (
    <TemporaryLayout noOverview={true}>
      <div className="LoanServicingExtensionSign-container">
        <div className="px-2 mt-6">
          <Overview title={`Loan Extension - ${id}`} hasBackButton={false} />
          <div className="max-w-screen-2xl m-auto">
            <LayoutLoading show={loading} />
            {signatureBlock()}
            <div className="relative bg-white shadow1 rounded mb-6 p-4">
              <div
                className="w-fit bg-yellow-100 border mt-2 border-yellow-400 text-yellow-700 px-4 py-3 rounded relative mb-4 text-[14px]"
                role="alert"
              >
                <div className="font-variation-settings-600">
                  - Request has been sent by <span className="underline">{requestedBy.by}</span> on{' '}
                  {formatTime(requestedBy.createdAt)}.
                </div>
              </div>

              <div className={`grid gap-4 md:grid-cols-4 grid-cols-1 mb-3 max-w-[960px]`}>
                {Object.keys(inputs).map((key) => {
                  let input = inputs[key]
                  if (!input || input.visible === false) return null
                  if (isPaymentLocked) input.disabled = true
                  return (
                    <div className={`input md:col-span-${input.span || 1}`} key={key}>
                      <RenderInput input={input} Key={key} onChange={onChange} />
                    </div>
                  )
                })}
              </div>
              {!isPaymentLocked && (
                <div
                  className="w-fit bg-gray-100 border mt-4 border-gray-400 text-gray-700 px-4 py-3 rounded relative text-[14px]"
                  role="alert"
                >
                  After payment, you <span className="font-bold italic">cannot edit</span> the above information, so
                  please double-check its accuracy and ensure <span className="font-bold italic">all documents</span>{' '}
                  are uploaded correctly.
                </div>
              )}
              {isPaymentLocked && (
                <div
                  className="w-fit bg-blue-50 border mt-4 border-blue-300 text-blue-800 px-4 py-3 rounded relative text-[14px]"
                  role="alert"
                >
                  Payment already submitted for this extension.
                </div>
              )}
              {!isPaymentLocked && payData && (
                <StripePay
                  data={payData}
                  onPay={onPay}
                  onTypeChange={(type) => onChange('feasibilityType', type)}
                />
              )}

              <div className="mt-6 w-fit">
                <div className="font-bold ml-2">Payment Histories</div>
                <PlainTable header={['No', 'Type', 'Status', 'Created At', 'View']} data={historiesTableData} />
              </div>
            </div>
            {stripeIntent && <StripeIntentDialog data={stripeIntent} onClose={() => setStripeIntent(null)} />}
          </div>
        </div>
      </div>
    </TemporaryLayout>
  )
}
